import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { TGetInstantwinQueue, TInstantwinMessage } from '@/typings/api/twitter_instantwin_queues.type'

type TGetQueueMessageParams = {
  sortBy: string
  limitCount: number
  pagingNo: number
  status: string
}

type TGetCheckExistQueuesParams = {
  accountId: string
  actionDatetime: string
  queueId?: number
  campaignType?: string
}

export type TwitterInstantwinQueueState = {
  loading: boolean
  queues: {
    list: TGetInstantwinQueue[]
    params: { campaignId: number; sortBy: string }
  }
  queueMessages: {
    queueId: number
    list: TInstantwinMessage[]
    total: number
    params: TGetQueueMessageParams
  }
  isExistQueue: boolean
}

const createInitialState = (): TwitterInstantwinQueueState => ({
  loading: true,
  queues: {
    list: [],
    params: {
      campaignId: 0,
      sortBy: '-actionDatetime'
    }
  },
  queueMessages: {
    queueId: 0,
    list: [],
    total: 0,
    params: {
      sortBy: '-actionDatetime',
      limitCount: 25,
      pagingNo: 1,
      status: ''
    }
  },
  isExistQueue: false
})

export const useTwitterInstantwinQueueStore = defineStore('twitterInstantwinQueueStore', () => {
  const initialState = createInitialState()

  const loading = ref<TwitterInstantwinQueueState['loading']>(initialState.loading)
  const queues = ref<TwitterInstantwinQueueState['queues']>(initialState.queues)
  const queueMessages = ref<TwitterInstantwinQueueState['queueMessages']>(initialState.queueMessages)
  const isExistQueue = ref<TwitterInstantwinQueueState['isExistQueue']>(initialState.isExistQueue)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    loading.value = nextState.loading
    queues.value = nextState.queues
    queueMessages.value = nextState.queueMessages
    isExistQueue.value = nextState.isExistQueue
  }

  const setLoading = (newLoading: boolean): void => {
    loading.value = newLoading
  }

  const fetchQueueList = async (): Promise<void> => {
    if (!queues.value.params.campaignId || !queues.value.params.sortBy) {
      return
    }

    loading.value = true

    const result = await api.get('/twitter_instantwin_queues', {
      params: queues.value.params
    })

    loading.value = false

    if (!result.data) {
      return
    }

    queues.value.list = result.data
  }

  const fetchQueueMessage = async (): Promise<void> => {
    if (!queueMessages.value.queueId) {
      return
    }

    loading.value = true

    const queueId = queueMessages.value.queueId

    const result = await api.get(`/twitter_instantwin_queues/${queueId}/messages`, {
      params: queueMessages.value.params
    })

    loading.value = false

    if (!result.data) {
      return
    }

    queueMessages.value.list = result.data.messages
    queueMessages.value.total = result.data.dataCount
  }

  const fetchCheckExistQueues = async (params: TGetCheckExistQueuesParams): Promise<void> => {
    const result = await api.get('/twitter_queues/check_exist_queues', {
      params
    })

    if (result.error) {
      return
    }

    isExistQueue.value = result.data
  }

  const setQueueListParams = (newParams: Partial<{ campaignId: number; sortBy: string }>): void => {
    queues.value.params = {
      ...queues.value.params,
      ...newParams
    }
  }

  const setQueueMessageParams = (newParams: Partial<TGetQueueMessageParams>): void => {
    queueMessages.value.params = {
      ...queueMessages.value.params,
      ...newParams
    }
  }

  const setQueueMessageId = (queueId: number): void => {
    queueMessages.value.queueId = queueId
  }

  return {
    loading: readonly(loading),
    queues: readonly(queues),
    queueMessages: readonly(queueMessages),
    isExistQueue: readonly(isExistQueue),
    reset,
    setLoading,
    fetchQueueList,
    fetchQueueMessage,
    fetchCheckExistQueues,
    setQueueListParams,
    setQueueMessageParams,
    setQueueMessageId
  }
})
